/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { CircularProgress, Grid, Typography } from '@mui/material';

import { AlignCenter } from '../../../../../../components/AlignCenter/AlignCenter';
import { Namespaces } from '../../../../../../i18n';
import { ClusterUpdateRequestBody } from '../../../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import {
  fetchDatabaseListOfCluster,
  fetchPgHbaOfCluster,
  fetchUserListOfCluster,
} from '../../../../../../utils/queryFns';
import { useProjectPermissions } from '../../../../../../utils/useProjectPermissions';
import { PgHbaList } from './components/PgHbaList/PgHbaList';
import { usePgHbaStyles } from './PgHba.styles';

type PgHbaProps = {
  updateData: (data: ClusterUpdateRequestBody) => Promise<unknown>;
  identificationData: UpdateClusterDetailsParameterIdentification;
};

export const PgHba = memo<PgHbaProps>(props => {
  const { updateData, identificationData } = props;
  const classes = usePgHbaStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const pgHbaQuery = useQuery([fetchPgHbaOfCluster.key, { ...identificationData }], fetchPgHbaOfCluster.fn, {
    refetchOnWindowFocus: false,
  });

  const databaseListQuery = useQuery(
    [fetchDatabaseListOfCluster.key, { ...identificationData }],
    fetchDatabaseListOfCluster.fn,
    { refetchOnWindowFocus: false },
  );

  const userListQuery = useQuery([fetchUserListOfCluster.key, { ...identificationData }], fetchUserListOfCluster.fn, {
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => pgHbaQuery.data?.data.payload || [], [pgHbaQuery.data]);

  const databaseListData = useMemo(() => databaseListQuery.data?.data.payload || [], [databaseListQuery.data]);

  const userListData = useMemo(() => userListQuery.data?.data.payload.users || [], [userListQuery.data]);

  return pgHbaQuery.isLoading || databaseListQuery.isLoading || userListQuery.isLoading ? (
    <AlignCenter>
      <CircularProgress color="primary" size={40} />
    </AlignCenter>
  ) : (
    <Grid container>
      <Grid container classes={{ root: classes.header }}>
        <Grid item xs={2}>
          <Typography className={classes.headerItem}>{t('details.acl.connection_type')}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.headerItem}>{t('details.acl.database')}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.headerItem}>{t('details.acl.user')}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography className={classes.headerItem}>{t('details.acl.address')}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography className={classes.headerItem}>{t('details.acl.auth_method')}</Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <DndProvider backend={HTML5Backend}>
          <PgHbaList
            updateData={updateData}
            data={data}
            databaseList={databaseListData}
            userList={userListData}
            isEditable={permissions.edit_cluster}
          />
        </DndProvider>
      </Grid>
    </Grid>
  );
});
